import type { GetServerSideProps } from 'next';

import { getLocaleFromParams } from '@/utils/locale';

import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { getIndexName } from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StoreHit } from '@/integrations/algolia/types';
import {
  BoutiqueDetailCMSTypeEnum,
  fetchCMSBoutiqueDetailContentWithExtraData,
} from '@/lib/boutique/helpers';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { resolveContentDraftMode } from '@/lib/draftMode';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType } from '@/lib/revalidate';
import { AppointmentView } from '@/views/AppointmentView';

interface StoreStaticProps {
  resultId?: string;
  seoConfig?: any;
  store?: StoreHit;
}

export const getServerSideProps: GetServerSideProps<StoreStaticProps> =
  commonServerSideProps(async (context) => {
    const draftMode = resolveContentDraftMode(context);
    const storeSlug = context.params?.storeslug as string;
    const routerLocale = getLocaleFromParams(context?.params);
    const { country, locale } = getRegionFromLocale(routerLocale);
    const { countryCode } = getContentfulRegionFromLocale(routerLocale);
    const contentfulLocale = locale.contentful ?? locale.locale;
    try {
      const algoliaSsrSearchClient = createSearchClient({
        isPreview: draftMode,
        isSsr: true,
      });
      const indexName = getIndexName('STORE', routerLocale);
      const index = algoliaSsrSearchClient.initIndex(indexName);

      // Single server-side fetch using browseObjects with slug + offers_appointment + web_visible
      let store: StoreHit | null = null;
      await index.browseObjects<StoreHit>({
        facetFilters: [
          `slug:${storeSlug}`,
          `offers_appointment:true`,
          `web_visible:true`,
        ],
        attributesToRetrieve: [
          'appointments',
          'utc_offset',
          'temporary_hours',
          'photo_gallery',
          'name',
          'name_en',
          'name_fr',
          'name_de',
          'name_it',
          'name_es',
          'name_ja',
          'name_ko',
          'name_pt',
          'name_zh_Hans',
          'name_zh_Hant',
          'slug',
          'store_id',
          'hours',
        ],
        batch: (batch) => {
          if (!store && batch && batch.length > 0) {
            store = batch[0] as StoreHit;
          }
        },
      });

      if (!store) {
        const destination = `/${routerLocale}/stores/breitling-boutiques/${countryCode}/${storeSlug}`;
        return {
          redirect: {
            destination,
            permanent: false,
          },
        };
      }

      const resolvedStore = store as StoreHit;

      const { seoConfigBuilder } =
        await fetchCMSBoutiqueDetailContentWithExtraData(
          resolvedStore.store_id,
          countryCode,
          locale.code,
          country.channelSlug,
          contentfulLocale,
          BoutiqueDetailCMSTypeEnum.Appointment,
        );

      const newSeoVariables = {
        boutiqueName: getTranslatedStoreValue(
          resolvedStore,
          'name',
          locale.slug,
        ),
      };
      seoConfigBuilder.mergeSeoVariables(newSeoVariables);

      return {
        props: {
          resultId: resolvedStore.store_id,
          store: resolvedStore,
          seoConfig: seoConfigBuilder?.setNoIndex().build(),
        },
      };
    } catch (error) {
      return {
        notFound: true,
      };
    }
  }, CacheHeaderType.NO_CACHE);
export default AppointmentView;
